/**
 * useAnalytics - Custom React hook for analytics tracking
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    trackAPICall,
    trackAuthentication,
    trackButtonClick,
    trackError,
    trackEvent,
    trackFormSubmission,
    trackNavigation,
    trackPageView
} from '../lib/analytics';

export const useAnalytics = () => {
  const location = useLocation();
  const previousPath = useRef<string>(location.pathname);

  // Track page views on navigation
  useEffect(() => {
    const currentPath = location.pathname;
    
    if (previousPath.current && previousPath.current !== currentPath) {
      // Extract page name from path
      const pathSegments = currentPath.split('/').filter(Boolean);
      const pageName = pathSegments[pathSegments.length - 1] || 'dashboard';
      
      trackNavigation(previousPath.current, currentPath, {
        page_name: pageName,
      });
    }

    previousPath.current = currentPath;
  }, [location.pathname]);

  // Track page view on component mount
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const pageName = pathSegments[pathSegments.length - 1] || 'dashboard';
    
    trackPageView(pageName, location.pathname);
  }, [location.pathname]);

  return {
    trackEvent,
    trackButtonClick: useCallback(
      (buttonName: string, metadata?: Record<string, any>) => {
        trackButtonClick(buttonName, metadata);
      },
      []
    ),
    trackNavigation: useCallback(
      (fromPath: string, toPath: string, metadata?: Record<string, any>) => {
        trackNavigation(fromPath, toPath, metadata);
      },
      []
    ),
    trackFormSubmission: useCallback(
      (formName: string, formData: Record<string, any>, metadata?: Record<string, any>) => {
        trackFormSubmission(formName, formData, metadata);
      },
      []
    ),
    trackPageView: useCallback(
      (pageName: string, pagePath?: string) => {
        trackPageView(pageName, pagePath || location.pathname);
      },
      [location.pathname]
    ),
    trackAPICall: useCallback(
      (method: string, endpoint: string, status: number, metadata?: Record<string, any>) => {
        trackAPICall(method, endpoint, status, metadata);
      },
      []
    ),
    trackError: useCallback(
      (errorMessage: string, errorType?: string, metadata?: Record<string, any>) => {
        trackError(errorMessage, errorType, metadata);
      },
      []
    ),
    trackAuthentication: useCallback(
      (action: string, metadata?: Record<string, any>) => {
        trackAuthentication(action, metadata);
      },
      []
    ),
  };
};
